'use client';

import Image from 'next/image';
import { useCallback } from 'react';

import { CloseIcon } from '@/components/icons/close';
import { cn } from '@/lib/utils';
import { useAdImpression, useArticleBannerVisibility, useTrackAdEvent } from '../../hooks';
import type { AdCampaignDto } from '../../types';

type ArticleStickyBannerProps = {
  campaign: AdCampaignDto;
};

export const ArticleStickyBanner = ({ campaign }: ArticleStickyBannerProps) => {
  const { trackAdEvent } = useTrackAdEvent();
  const { isVisible, dismiss } = useArticleBannerVisibility();
  const { elementRef } = useAdImpression({
    campaignId: campaign.id,
    enabled: isVisible,
  });

  const mediaUrl = campaign.media.url;
  const isGif = campaign.media.mimeType === 'image/gif';

  const handleClick = useCallback(() => {
    void trackAdEvent(campaign.id, 'click');
  }, [campaign.id, trackAdEvent]);

  if (!mediaUrl || !campaign.promoUrl || !isVisible) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 md:hidden',
        'pointer-events-none flex h-[100px] justify-center pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <div
        ref={elementRef}
        className="pointer-events-auto relative flex h-full w-full max-w-[352px] flex-col justify-end"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Закрыть рекламный баннер"
          className="absolute right-1 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/40"
        >
          <CloseIcon className="h-4 w-4 [&_path]:fill-white" />
        </button>

        <a
          href={campaign.promoUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="relative block h-[55px] w-full overflow-hidden"
        >
          <Image
            src={mediaUrl}
            alt="Рекламный баннер"
            fill
            className="object-cover"
            sizes="(max-width: 352px) 100vw, 352px"
            loading="lazy"
            quality={75}
            unoptimized={isGif}
          />
        </a>
      </div>
    </div>
  );
};
